import MD5 from 'crypto-js/md5';
import { FileBasicInfo } from '../redux/import/sliceImportFileUpload';
import { IProjectFileType } from '../redux/project/sliceProjectFileTypes';
import store from '../redux/store';
import {
    showWebDAVDecisionDialog,
} from '../redux/project/file/sliceProjectFileOpen';

export const handleFileOpenCheck = (check: {
    response: string;
    callbackURL: string;
}) => {
    if (['WEBDAV_VIEW', 'WEBDAV_LOCKED', 'WEBDAV_DECIDE'].some(x => check.response.startsWith(x))) {
        store.dispatch(
            showWebDAVDecisionDialog({
                mode: check.response,
                callbackURL: check.callbackURL,
            }),
        );
    } else {
        window.open(check.callbackURL);
    }
};

export const getFileExtensionsFromFormats = (fileFormats: string[]): string[] => {
    const fileTypes = store.getState().projectFileTypes.data ?? [];

    const allowedExtensions = fileTypes
        .filter(x => fileFormats.some(allowed => x.name.toLowerCase() === allowed.toLowerCase()))
        .flatMap(x => x.extensions.split('|').map(ext => '.' + ext.trim().toLowerCase()));

    return [...new Set(allowedExtensions)].sort();
};

export const getFileTypeOrDefault = (fileName: string): IProjectFileType => {
    const { data: fileTypes, default: fallback } = store.getState().projectFileTypes;

    if (fileName) {
        const fileExtension = getFileExtension(fileName).toLowerCase();
        const fileType = fileTypes.find(x =>
            x.extensions?.split('|')?.some(extension => extension.toLowerCase() === fileExtension),
        );

        if (fileType) {
            return fileType;
        }
    }

    return fallback;
};

/**
 * Get filename without extension.
 */
export const getFilenameWithoutExtension = (fileName: string): string => {
    return fileName.split('.').slice(0, -1).join('.') || fileName;
};

export const getFileExtension = (fileName: string): string => {
    return fileName?.split('.')?.pop() ?? '';
};

export const getMagicBytes = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
        const fileReader = new FileReader();
        fileReader.onerror = () => reject();
        fileReader.onload = (e: ProgressEvent<FileReader>) => {
            if (!e.target) {
                reject();
            }

            const buffer = e.target.result as ArrayBuffer;
            const hex = [...new Uint8Array(buffer)]
                .map(byte => byte.toString(16).padStart(2, '0'))
                .join(' ')
                .toUpperCase();

            resolve(hex);
        };

        fileReader.readAsArrayBuffer(file.slice(0, 5));
    });
};

export const hashNameAndSize = (file: File): string => {
    return MD5(file.name + file.size.toString()).toString();
};

export const getFileBasicInfo = async (file: File): Promise<FileBasicInfo> => {
    return {
        fileName: file.name,
        fileSize: file.size,
        magicBytes: await getMagicBytes(file),
    };
};

/**
 * Converts a file size (in bytes) to its closest human-readable file size (KB/MB/GB).
 * @param size - Raw file size.
 * @returns Processed file size.
 */
export const processFileSize = (size: string | number): string => {
    ['B', 'KB', 'MB', 'GB'].forEach((ending: string) => {
        if (!isNaN(+size)) {
            size =
                +size < 512 ? `${size} ${ending}` : (parseFloat(size as string) / 1024).toFixed(2);
        }
    });

    return size as string;
};

/**
 * Converts a file data string from Notes into an IFileInfo object.
 * @param fileInfo - File data string.
 * @param processFile - (Optional) Flag to automatically convert the file's size into a human-readable format.
 * @returns IFileInfo Object.
 */
export const getFileInfo = (fileInfo: string, processFile?: boolean): IFileInfo => {
    const [fileName, size, index, type, name, hash] = fileInfo.split('|');

    return {
        fileName,
        type,
        name,
        hash,
        size: processFile ? processFileSize(size) : size,
        index: +index,
    };
};

/**
 * Download document
 * @param blob - Blob.
 * @param fileName - Name of the downloaded file
 */
export const downloadDocumentsStream = (blob: Blob, fileName: string): void => {
    const link = document.createElement('a');

    const url = URL.createObjectURL(blob);
    link.setAttribute('download', fileName);
    link.style.display = 'none';
    document.body.appendChild(link);

    link.setAttribute('href', url);
    link.click();
    URL.revokeObjectURL(url);
    document.body.removeChild(link);
};

/**
 * Copy url using clipboard.
 * @param text Copied url
 * @param success Success callback.
 * @param error Error callback.
 * @param browserError Browser's error callback.
 */
export const handleCopyText = async (
    text: string,
    success: () => void,
    error: () => void,
    browserError: () => void,
) => {
    if (!navigator.clipboard) {
        browserError();
    }
    try {
        await navigator.clipboard.writeText(text);
        success();
    } catch (e) {
        error();
    }
};

/**
 * Interface that describes a file attachment in Lotus Notes.
 */
export interface IFileInfo {
    /** Full File Name. */
    fileName: string;

    /** File Size. */
    size: string;

    /** File Index. */
    index: number;

    /** File Type. */
    type: string;

    /** Alternative File Name. */
    name: string;

    /** Document Hash Id. */
    hash: string;
}
